/**
 * @file close-site-model.ts
 * @description Модель окна «Закрыть сайт для покупателей» (phase19.16, SB138,
 * SB57; uiux-site-closed.md §3; db-design-site-closed.md §3): значения
 * формы, проверка до отправки (длина текста, цифры скидок, «откроем
 * примерно в» — позже «сейчас» и не дальше 7 дней), тело запроса и подпись
 * «Откроется сам сегодня в 15:20» по часовому поясу магазина.
 *
 * Пустой текст — сервер подставит текст по умолчанию. Срок автооткрытия
 * сервер считает сам по своим часам; здесь — только подпись для человека.
 */

import {
  SITE_CLOSURE_AUTO_OPEN,
  SITE_CLOSURE_EXPECTED_MAX_DAYS,
  SITE_CLOSURE_MESSAGE_MAX,
  siteClosureDiscountTerm,
  siteClosureMessageLength,
  type SiteClosureAutoOpen,
  type SiteClosureCloseRequest,
} from "@chaiberi/shared";

import { zonedClock, zonedDayIso, zonedDayWord, zonedToDate } from "../../lib/site-access/site-access-time";

/** Значения формы окна. */
export interface CloseSiteForm {
  message: string;
  /** «чч:мм» по времени магазина или пусто (необязательно). */
  expectedTime: string;
  /** «гггг-мм-дд» по времени магазина. */
  expectedDay: string;
  autoOpen: SiteClosureAutoOpen;
}

/** Ошибки полей; пустой объект — можно отправлять. */
export interface CloseSiteErrors {
  message?: string;
  expected?: string;
}

/** Начальные значения: текст пустой (умолчание сервера), срок 1 ч, день — сегодня у магазина. */
export function initialCloseSiteForm(now: Date, zone: string): CloseSiteForm {
  return { message: "", expectedTime: "", expectedDay: zonedDayIso(now, zone), autoOpen: "1h" };
}

/** Варианты «Открыть автоматически» (реестр подписей, порядок — как в проекте). */
export const AUTO_OPEN_LABELS: Readonly<Record<SiteClosureAutoOpen, string>> = {
  "1h": "через 1 ч",
  "3h": "через 3 ч",
  none: "без срока",
};

/** Предупреждение при «без срока» (uiux §3). */
export const NO_DEADLINE_WARNING =
  "Сайт останется закрытым, пока вы не откроете его сами. Поисковики могут понизить сайт, если пауза затянется дольше суток.";

/** Подсказка в окне: правки конструктора и так не видны до публикации. */
export const BUILDER_DRAFT_HINT = "Правки в конструкторе не видны покупателям до публикации — для этого закрывать сайт не нужно.";

/** Ввод времени по маске «чч:мм»: только цифры, двоеточие ставится само. */
export function maskClockInput(raw: string): string {
  const digits = raw.replace(/\D/gu, "").slice(0, 4);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/** «чч:мм» с часами 0–23 и минутами 0–59. */
export function isValidClock(value: string): boolean {
  const m = /^(\d{2}):(\d{2})$/u.exec(value);
  return !!m && Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

/** Момент «откроем примерно в» или `null` (поле пустое или неверное). */
export function expectedOpenDate(form: CloseSiteForm, zone: string): Date | null {
  if (!isValidClock(form.expectedTime) || !form.expectedDay) return null;
  return zonedToDate(form.expectedDay, form.expectedTime, zone);
}

/** Проверка до отправки — те же правила, что у сервера (SB57, §3). */
export function validateCloseSite(form: CloseSiteForm, now: Date, zone: string): CloseSiteErrors {
  const errors: CloseSiteErrors = {};
  const text = form.message.trim();
  if (siteClosureMessageLength(text) > SITE_CLOSURE_MESSAGE_MAX) {
    errors.message = `Текст длиннее ${SITE_CLOSURE_MESSAGE_MAX} знаков — сократите его.`;
  } else {
    const term = siteClosureDiscountTerm(text);
    if (term) errors.message = `Размер скидки в этом тексте указывать нельзя — уберите «${term}».`;
  }
  if (form.expectedTime !== "") {
    const at = expectedOpenDate(form, zone);
    if (!at) errors.expected = "Время — в виде чч:мм, например 17:00.";
    else if (at.getTime() <= now.getTime()) errors.expected = "Это время уже прошло — укажите позже или выберите другой день.";
    else if (at.getTime() > now.getTime() + SITE_CLOSURE_EXPECTED_MAX_DAYS * 86_400_000) {
      errors.expected = `Не дальше чем через ${SITE_CLOSURE_EXPECTED_MAX_DAYS} дней.`;
    }
  }
  return errors;
}

/** Есть ли ошибки. */
export function hasCloseSiteErrors(errors: CloseSiteErrors): boolean {
  return Object.values(errors).some(Boolean);
}

/** Тело `POST /admin/site-access/close`: пустой текст — `null` (умолчание сервера). */
export function closeSiteRequest(form: CloseSiteForm, zone: string): SiteClosureCloseRequest {
  const text = form.message.trim();
  return {
    message: text === "" ? null : text,
    expectedOpenAt: expectedOpenDate(form, zone)?.toISOString() ?? null,
    autoOpen: form.autoOpen,
  };
}

/** «Откроется сам сегодня в 15:20»; без срока — `null`. */
export function autoOpenPreview(autoOpen: SiteClosureAutoOpen, now: Date, zone: string): string | null {
  const sec = SITE_CLOSURE_AUTO_OPEN[autoOpen];
  if (sec === null) return null;
  const at = new Date(now.getTime() + sec * 1000);
  return `Откроется сам ${zonedDayWord(at, now, zone)} в ${zonedClock(at, zone)}`;
}

/** Подпись дня под полем времени: «сегодня», «завтра», «21.10». */
export function expectedDayWord(form: CloseSiteForm, now: Date, zone: string): string {
  const at = zonedToDate(form.expectedDay, "12:00", zone);
  return at ? zonedDayWord(at, now, zone) : "";
}
